import '../collection-action-menu/collection-action-menu.js';
import {css, CSSResult, html, LitElement, nothing, unsafeCSS} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {CollectionTrackingContext, Messages, WishlistCollection} from '../../../@types/wishlist.js';
import {consume} from '@lit/context';
import typographyStyles from '../../../styles/responsive-nui-typography.scss';
import styles from './wishlist-collection-head.scss';
import eyeClosed from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_eye-closed.svg';
import eye from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_eye.svg';
import sharedIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_send.svg';
import {collectionMenuContext} from '../../../scripts/context/menuContext.js';
import {CollectionMenuState} from '../../../scripts/context/menu-state.js';
import {classMap} from 'lit/directives/class-map.js';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {addTitleToIcon} from '../icon/icon-accessibility.js';

@customElement('ents-wishlist-collection-head')
export class WishlistCollectionHead extends LitElement {
  @consume({context: i18nContext})
  protected messages!: Messages;

  @property({type: Boolean, attribute: 'show-share-icon', reflect: true})
  private showShareStatusIcon: boolean = false;

  @property({type: Boolean, attribute: 'with-headline', reflect: true})
  private withHeadline: boolean = false;

  @property({type: Object, attribute: 'collection-tracking-context'})
  private collectionTrackingContext!: CollectionTrackingContext;

  @property({type: Array, attribute: 'wishlist-collection'})
  private wishlistCollection!: WishlistCollection;

  @consume({context: collectionMenuContext, subscribe: true})
  @property({attribute: false})
  private collectionMenuState!: CollectionMenuState;

  static get styles(): CSSResult[] {
    return [css`${unsafeCSS(styles)}`, css`${unsafeCSS(typographyStyles)}`];
  }

  private openActionMenuLayer(col: WishlistCollection) {
    this.collectionMenuState.openActionMenu(col, this.collectionTrackingContext);
  }

  private openShareMenuLayer(col: WishlistCollection) {
    this.collectionMenuState.openShareView(col, this.collectionTrackingContext);
  }

  connectedCallback() {
    super.connectedCallback();

    this.dispatchEvent(new Event('scroll-top'));
  }

  render() {
    const shareStatusIcon = html`${this.wishlistCollection.isSharingAllowed ?
      addTitleToIcon(eye(), this.messages['ents.wishlist.collection.share.state.shared']) :
      addTitleToIcon(eyeClosed(), this.messages['ents.wishlist.collection.share.state.private'])}`;
    return html`
      <div
        class="ents-wishlist-collection-head ${classMap({'ents-wishlist-collection-head--with-headline': this.withHeadline})}">
        <span class="ents-wishlist-collection-head__name ents-copy-bold fs-mask"
              data-test-ents-wishlist-collection-name>
          ${this.showShareStatusIcon ? html`<span class="ents-wishlist-collection-head__name--icon">
            ${this.showShareStatusIcon ? shareStatusIcon : nothing}
          </span>` : nothing}
          <span class="ents-wishlist-collection-head__name--text">
            ${this.wishlistCollection.name}
          </span>
        </span>
        ${this.withHeadline ? html`
          <button
            class="ents-wishlist-collection-head__share--button"
            type="button"
            @click="${(event: Event) => {
              event.preventDefault();
              this.openShareMenuLayer(this.wishlistCollection);
            }}"
            data-test-ents-wishlist-collection-head-share-button
          >
            ${addTitleToIcon(sharedIcon(), this.messages['ents.wishlist.collection.actionmenu.share'])}
          </button>
        ` : nothing}
        <button
          class="ents-wishlist-collection-head__action-menu--button"
          type="button"
          aria-label="${this.messages['ents.wishlist.collection.actionmenu.button.description']}"
          @click="${(event: Event) => {
            event.preventDefault();
            this.openActionMenuLayer(this.wishlistCollection);
          }}"
          data-test-ents-wishlist-collection-action-button
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
               aria-label="${this.messages['ents.wishlist.collection.actionmenu.button.description']}">
            <title>${this.messages['ents.wishlist.collection.actionmenu.button.description']}</title>
            <circle cx="4.667" cy="12" r="2" fill="currentColor"/>
            <circle cx="12.667" cy="12" r="2" fill="currentColor"/>
            <circle cx="20.667" cy="12" r="2" fill="currentColor"/>
          </svg>
        </button>
      </div>
    `;
  }
}
